import { useState } from "react";
import { ChevronDownIcon } from "lucide-react";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TableCell } from "@/components/ui/table";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useCreateAddCommissionLedgerEntry } from "@/hooks/createAdds/useCreateAddCommissionLedgerEntry";
import { earningsTracker } from "@/lib/earningsTracker";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  CommissionLedgerEntryType,
  EarningsPaymentStatus,
  EarningsPlacement,
} from "@/types";

const paymentStatusToBadge: Record<
  EarningsPaymentStatus,
  { className: string; label: string }
> = {
  [EarningsPaymentStatus.UNPAID]: {
    className: "border-slate-200 bg-slate-100 text-slate-700",
    label: "Unpaid",
  },
  [EarningsPaymentStatus.PAID]: {
    className: "border-green-200 bg-green-100 text-green-700",
    label: "Paid",
  },
  [EarningsPaymentStatus.CLAWBACK]: {
    className: "border-amber-200 bg-amber-100 text-amber-800",
    label: "Clawback",
  },
};

// Status column: Unpaid rows flip to Paid, Paid rows flip to Clawback, and
// everything else (not started, superseded, already clawed) is a static badge.
export const EarningsTrackerStatusCell = ({
  placement,
  viewedUserId,
}: {
  placement: EarningsPlacement;
  viewedUserId?: number;
}) => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createLedgerEntry,
    isPending,
    isError,
  } = useCreateAddCommissionLedgerEntry();

  const { userNameMap } = useTaggableUsers();

  /* React Hooks */
  const [isPaidDialogOpen, setIsPaidDialogOpen] = useState(false);
  const [isClawbackDialogOpen, setIsClawbackDialogOpen] = useState(false);

  /* Helpers */
  const paymentStatus = earningsTracker.getPaymentStatus(placement);
  const badge = paymentStatusToBadge[paymentStatus];
  const commissionDollars = earningsTracker.getCommissionDollars(placement);
  // Superseded rows are frozen once unpaid, but a paid one still needs its clawback
  const isChangeable =
    !!placement.dateStarted &&
    paymentStatus !== EarningsPaymentStatus.CLAWBACK &&
    (!placement.isSuperseded || paymentStatus === EarningsPaymentStatus.PAID);
  const stafferName =
    viewedUserId !== undefined ? userNameMap[viewedUserId] : "this staffer";

  /* Handlers */
  const handleConfirmPaid = () => {
    mixpanelUtils.trackButtonClick(
      "Earnings mark placement paid",
      MIXPANEL_SCREENS.EARNINGS,
    );
    createLedgerEntry(
      {
        placementId: placement.id,
        type: CommissionLedgerEntryType.PAID,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        amount: commissionDollars || 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        commissionRate: placement.commissionRate || undefined,
      },
      {
        onSuccess: () => {
          setIsPaidDialogOpen(false);
        },
      },
    );
  };

  const handleConfirmClawback = () => {
    mixpanelUtils.trackButtonClick(
      "Earnings claw back placement",
      MIXPANEL_SCREENS.EARNINGS,
    );
    createLedgerEntry(
      {
        placementId: placement.id,
        type: CommissionLedgerEntryType.CLAWBACK,
        // Full clawback: reverse the ledger's current net
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        amount: -(commissionDollars || 0),
      },
      {
        onSuccess: () => {
          setIsClawbackDialogOpen(false);
        },
      },
    );
  };

  /* Render */
  const render = () => {
    if (!isChangeable) {
      return (
        <TableCell>
          <Badge
            variant="secondary"
            className={cn(
              "h-6 cursor-default whitespace-nowrap py-0",
              badge.className,
            )}
          >
            {badge.label}
          </Badge>
        </TableCell>
      );
    }

    const isUnpaid = paymentStatus === EarningsPaymentStatus.UNPAID;

    return (
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="h-auto rounded-full p-0 hover:bg-transparent"
            >
              <Badge
                variant="secondary"
                className={cn(
                  "h-6 cursor-pointer gap-1 whitespace-nowrap py-0",
                  badge.className,
                )}
              >
                {badge.label}
                <ChevronDownIcon className="h-3 w-3" />
              </Badge>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isUnpaid ? (
              <DropdownMenuItem
                onSelect={() => {
                  setIsPaidDialogOpen(true);
                }}
              >
                Paid
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="text-red-600 focus:text-red-600"
                onSelect={() => {
                  setIsClawbackDialogOpen(true);
                }}
              >
                Clawback
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <ConfirmationDialog
          open={isPaidDialogOpen}
          onOpenChange={setIsPaidDialogOpen}
          title="Mark commission paid"
          description={`You are confirming that you have been paid out ${formatting.currency(
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            commissionDollars || 0,
          )} for the placement of ${placement.candidateName} at ${placement.companyName}.`}
          confirmButtonText="Mark paid"
          isLoading={isPending}
          isError={isError}
          errorMessage="Failed to mark paid. Try again."
          onConfirm={handleConfirmPaid}
          variant="square"
        />
        <ConfirmationDialog
          open={isClawbackDialogOpen}
          onOpenChange={setIsClawbackDialogOpen}
          title="Claw back commission"
          description={`You are confirming that the money earned by ${stafferName} is to be returned to Laborup and will be deducted out of their next paycheck.`}
          confirmButtonText="Claw back"
          confirmationButtonVariant="destructive"
          isLoading={isPending}
          isError={isError}
          errorMessage="Failed to claw back. Try again."
          onConfirm={handleConfirmClawback}
          variant="square"
        />
      </TableCell>
    );
  };

  return render();
};
